import { css, html, LitElement } from 'lit';
import type { CountdownTimer } from '@/shared/messages.ts';

export class BizVoting extends LitElement {
  static properties = {
    topic: { type: String },
    roundLabel: { type: String, attribute: 'round-label' },
    responseAId: { type: Number, attribute: 'response-a-id' },
    responseAText: { type: String, attribute: 'response-a-text' },
    responseBId: { type: Number, attribute: 'response-b-id' },
    responseBText: { type: String, attribute: 'response-b-text' },
    aVotes: { type: Number, attribute: 'a-votes' },
    bVotes: { type: Number, attribute: 'b-votes' },
    timer: { attribute: false },
    eligible: { type: Boolean },
    isHost: { type: Boolean, attribute: 'is-host' },
    myVoteId: { type: Number, attribute: 'my-vote-id' },
    myLockedIn: { type: Boolean, attribute: 'my-locked-in' },
    result: { attribute: false },
    _remaining: { state: true },
  };

  declare topic: string;
  declare roundLabel: string;
  declare responseAId: number;
  declare responseAText: string;
  declare responseBId: number;
  declare responseBText: string;
  declare aVotes: number;
  declare bVotes: number;
  declare timer: CountdownTimer | null;
  declare eligible: boolean;
  declare isHost: boolean;
  declare myVoteId: number;
  declare myLockedIn: boolean;
  declare result: { winnerId: number; aVotes: number; bVotes: number } | null;
  declare _remaining: number;

  #tickHandle: number | null = null;

  constructor() {
    super();
    this.topic = '';
    this.roundLabel = '';
    this.responseAId = 0;
    this.responseAText = '';
    this.responseBId = 0;
    this.responseBText = '';
    this.aVotes = 0;
    this.bVotes = 0;
    this.timer = null;
    this.eligible = true;
    this.isHost = false;
    this.myVoteId = 0;
    this.myLockedIn = false;
    this.result = null;
    this._remaining = 0;
  }

  static styles = css`
    :host {
      display: block;
      max-width: 36rem;
      margin: 2rem auto;
      padding: 0 1rem;
    }
    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    .topic {
      font-size: 1.1rem;
    }
    .round-label {
      color: var(--fg-dim, #a0a4ad);
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-top: 0.15rem;
    }
    .topic-block {
      display: flex;
      flex-direction: column;
    }
    .timer-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .timer {
      font-variant-numeric: tabular-nums;
      font-size: 1.2rem;
      color: var(--fg-dim, #a0a4ad);
    }
    .timer.paused {
      color: var(--accent, #5fbf73);
    }
    .pause-btn {
      background: transparent;
      border: 1px solid var(--border, #2c2e36);
      color: var(--fg, #ececec);
      padding: 0.15rem 0.5rem;
      border-radius: 4px;
      font: inherit;
      line-height: 1;
      cursor: pointer;
    }
    .responses {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
    }
    .card {
      background: var(--bg-card, #1f2128);
      border: 1px solid var(--border, #2c2e36);
      border-radius: 8px;
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      text-align: center;
      cursor: pointer;
      transition: border-color 100ms;
    }
    .card.selected {
      border-color: var(--accent, #5fbf73);
    }
    .card[disabled] {
      cursor: not-allowed;
      opacity: 0.55;
    }
    .card .text {
      min-height: 3em;
    }
    .card .votes {
      font-variant-numeric: tabular-nums;
      color: var(--fg-dim, #a0a4ad);
    }
    .winner {
      border-color: var(--accent, #5fbf73);
      background: rgba(95, 191, 115, 0.08);
    }
    .actions {
      display: flex;
      gap: 0.5rem;
      margin-top: 1rem;
      align-items: center;
    }
    .hint {
      color: var(--fg-dim, #a0a4ad);
      font-style: italic;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.#startTicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTicker();
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('timer')) {
      this.#stopTicker();
      this.#startTicker();
    }
  }

  render() {
    const result = this.result;
    const aIsWinner = result?.winnerId === this.responseAId;
    const bIsWinner = result?.winnerId === this.responseBId;
    const selecting = !result && this.eligible && !this.myLockedIn;

    const paused = !!this.timer?.paused;
    const matchupReady = !!(this.responseAId && this.responseBId);
    // Pre-vote-start (waiting out the round-intro delay): show topic + round
    // label, but no timer or response cards. The timer reappears once
    // vote-start lands.
    return html`
      <link rel="stylesheet" href="/static/style.css" />
      <header>
        <span class="topic-block">
          <span class="topic">${this.topic}</span>
          ${this.roundLabel
            ? html`<span class="round-label">${this.roundLabel}</span>`
            : null}
        </span>
        ${result || !matchupReady
          ? null
          : html`
            <span class="timer-group">
              <span class="timer ${paused ? 'paused' : ''}">${this._remaining}s</span>
              ${this.isHost
                ? html`
                  <button
                    class="pause-btn"
                    title="${paused ? 'Resume' : 'Pause'}"
                    @click=${() => this.#onTogglePause(paused)}
                  >
                    ${paused ? '▶' : '⏸'}
                  </button>
                `
                : null}
            </span>
          `}
      </header>

      <div class="responses">
        ${this.responseAId && this.responseBId
          ? html`
            ${this.#renderCard('a', this.responseAId, this.responseAText, this.aVotes, aIsWinner, selecting)}
            ${this.#renderCard('b', this.responseBId, this.responseBText, this.bVotes, bIsWinner, selecting)}
          `
          : null}
      </div>

      <div class="actions">
        ${!this.responseAId || !this.responseBId
          ? null
          : result
            ? html`<span class="hint">Winner: ${aIsWinner ? this.responseAText : this.responseBText} (${result.aVotes}&ndash;${result.bVotes})</span>`
            : !this.eligible
              ? html`<span class="hint">You'll be eligible starting with the next vote.</span>`
              : this.myLockedIn
                ? html`<span class="hint">Locked in.</span>`
                : html`
                    <button
                      ?disabled=${!this.myVoteId}
                      @click=${() => this.#onLockIn()}
                    >
                      Lock in
                    </button>
                  `}
      </div>
    `;
  }

  #renderCard(
    side: 'a' | 'b',
    id: number,
    text: string,
    votes: number,
    isWinner: boolean,
    selecting: boolean,
  ) {
    const selected = this.myVoteId === id;
    const classes = ['card'];
    if (selected) {
      classes.push('selected');
    }
    if (isWinner) {
      classes.push('winner');
    }
    return html`
      <div
        class=${classes.join(' ')}
        ?disabled=${!selecting}
        @click=${() => selecting && this.#onPick(id)}
      >
        <div class="text">${text}</div>
        <div class="votes">${votes} vote${votes === 1 ? '' : 's'}</div>
      </div>
    `;
  }

  #onPick(id: number) {
    this.dispatchEvent(
      new CustomEvent('biz-cast-vote', {
        detail: { responseId: id, lockedIn: false },
        bubbles: true,
        composed: true,
      }),
    );
  }

  #onLockIn() {
    if (!this.myVoteId) return;
    this.dispatchEvent(
      new CustomEvent('biz-cast-vote', {
        detail: { responseId: this.myVoteId, lockedIn: true },
        bubbles: true,
        composed: true,
      }),
    );
  }

  #onTogglePause(currentlyPaused: boolean) {
    this.dispatchEvent(
      new CustomEvent(currentlyPaused ? 'biz-unpause-game' : 'biz-pause-game', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  #startTicker() {
    if (!this.timer) return;
    // Anchor against the local monotonic clock at the moment we received the
    // timer message. Avoids cross-device clock skew — every client counts
    // down from "what the server said remained" using its own clock.
    const anchor = performance.now();
    const totalMs = this.timer.ms;
    const paused = this.timer.paused;
    if (paused) {
      this._remaining = Math.max(0, Math.ceil(totalMs / 1000));
      return;
    }
    const tick = () => {
      if (!this.timer) {
        this.#stopTicker();
        return;
      }
      const elapsedMs = performance.now() - anchor;
      this._remaining = Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000));
    };
    tick();
    this.#tickHandle = setInterval(tick, 250) as unknown as number;
  }

  #stopTicker() {
    if (this.#tickHandle !== null) {
      clearInterval(this.#tickHandle);
      this.#tickHandle = null;
    }
  }
}

customElements.define('biz-voting', BizVoting);
